import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from "@mui/material";
import AxiosInstance from "../../utils/axiosInstance";
import showToast from "../Toast/Toster";

const CONFIRMATION_MESSAGE =
  "Your request for email access has been sent to your company's Super Admins.";

/**
 * Modal shown when email is disabled for the company.
 * Displays a message and "Request Email Access" button.
 * On success, shows confirmation and closes.
 */
const EmailDisabledModal = ({ open, onClose, message }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState(CONFIRMATION_MESSAGE);

  const handleRequestAccess = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post(
        "/v1/company-email-settings/request-email-access"
      );
      if (response?.data?.statusCode === 200) {
        if (response?.data?.alreadySent) {
          showToast.warning(
            "You already requested email access recently. Please wait a few minutes before requesting again."
          );
          onClose();
          return;
        }
        const msg = response?.data?.message || CONFIRMATION_MESSAGE;
        setSuccess(true);
        setSuccessMessage(msg);
        showToast.success(msg);
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        showToast.error(
          response?.data?.message || "Failed to send request. Please try again."
        );
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        "Failed to send request. Please try again.";
      showToast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSuccess(false);
      onClose();
    }
  };

  const themeBlue = "#0f2b46";
  const themeBorder = "#e3e8ef";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: 0,
          boxShadow: "var(--shadow-lg)",
          border: `1px solid ${themeBorder}`,
        },
      }}
    >
      <DialogTitle
        style={{
          fontSize: "18px",
          fontWeight: 600,
          color: themeBlue,
          borderBottom: `1px solid ${themeBorder}`,
          padding: "16px 24px",
          backgroundColor: "#fff",
        }}
      >
        Email is currently disabled
      </DialogTitle>
      <DialogContent sx={{ padding: { xs: "16px 20px", sm: "20px 24px" }, backgroundColor: "#fff" }}>
        <Typography variant="body1" sx={{ color: "#333", lineHeight: 1.6 }} paragraph>
          {success
            ? successMessage
            : message ||
              "Email functionality is currently disabled for your company. Only a Super Admin can enable it."}
        </Typography>
        {!success && (
          <Typography variant="body2" sx={{ color: "#555" }}>
            You can request that your company&apos;s Super Admins enable email
            access by clicking the button below.
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        style={{
          padding: "16px 24px",
          justifyContent: "space-between",
          backgroundColor: "#fff",
          borderTop: `1px solid ${themeBorder}`,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          style={{
            color: themeBlue,
            borderColor: themeBlue,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {success ? "Close" : "Cancel"}
        </Button>
        {!success && (
          <Button
            onClick={handleRequestAccess}
            variant="contained"
            disabled={loading}
            style={{
              backgroundColor: themeBlue,
              color: "#fff",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {loading ? "Sending…" : "Request email access"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailDisabledModal;
